import { useState } from "react";
import { Button, Message } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { Modal } from "../../components/Modal/Modal";
import { cn } from "../../utils/bem";
import "./DiscoverModals.prefix.css";

const block = cn("leave-modal");
const CONFIRM_PHRASE = "OPT OUT";

/**
 * LeaveProjectModal — opt-out flow for a contributor leaving a project.
 * Shared ``<Modal>`` chrome; reminds them what they agreed to, what
 * happens to their work, and requires typing a confirm phrase so opting
 * out is deliberate. Deletes the ``ProjectContributor`` row but keeps the
 * contributor's submitted annotations (attribution retained).
 */
export const LeaveProjectModal = ({
  project,
  onClose,
  onLeft,
}: {
  project: { id: number; title: string | null; organization?: { title: string } | null };
  onClose: () => void;
  onLeft: (projectId: number) => void;
}) => {
  const api = useAPI();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const canConfirm = typed.trim().toUpperCase() === CONFIRM_PHRASE;

  const onConfirm = async () => {
    if (!canConfirm || busy) return;
    setError("");
    setBusy(true);
    const res = await api.callApi("projectLeave", { params: { pk: project.id }, suppressError: true });
    setBusy(false);
    if (!res || res.error) {
      setError(res?.error || "Couldn't opt out. Try again in a moment.");
      return;
    }
    onLeft(project.id);
  };

  return (
    <Modal
      visible
      onHide={onClose}
      title={`Opt out of ${project.title ?? "this project"}?`}
      style={{ width: 520 }}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button variant="neutral" look="outlined" size="small" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="negative" size="small" onClick={onConfirm} disabled={!canConfirm || busy}>
            {busy ? "Opting out…" : "Opt out"}
          </Button>
        </div>
      }
    >
      <div className={block.toClassName()}>
        {error ? (
          <Message variant="negative" size="small">
            {error}
          </Message>
        ) : null}

        <section className={block.elem("section").toClassName()}>
          <p className={block.elem("lead").toClassName()}>
            When you joined, you agreed to share your name, email, and submitted work with{" "}
            {project.organization?.title ? <strong>{project.organization.title}</strong> : "this project's owners"}, and
            to any consent agreements the project required.
          </p>
          <ul className={block.elem("list").toClassName()}>
            <li>Opting out removes your access to this project immediately.</li>
            <li>
              <strong>Annotations you've already submitted stay with the project</strong>, attribution is kept. Opting
              out doesn't withdraw those.
            </li>
            <li>You can join again later from Discover if the project is still open.</li>
          </ul>
        </section>

        <section className={block.elem("section").toClassName()}>
          <label className={block.elem("section-title").toClassName()} htmlFor="leave-confirm">
            Type <strong>{CONFIRM_PHRASE}</strong> to confirm
          </label>
          <input
            id="leave-confirm"
            className={block.elem("input").toClassName()}
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={busy}
            autoFocus
            placeholder={CONFIRM_PHRASE}
          />
        </section>
      </div>
    </Modal>
  );
};

export default LeaveProjectModal;
